import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut } from "firebase/auth";
import { auth, db } from "../firebase/fire";
import { collection, query, where, getDocs, getDoc, doc, setDoc, limit } from "firebase/firestore";
import { FaArrowAltCircleLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import LoadingOverlay from "./LoadingOverlay";
import Modal from "./Modal";
import { LogoContext } from "./LogoContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [errors, setErrors] = useState({});
  const [fullName, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [logoUrl, setLogoUrl] = useContext(LogoContext);

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    const re = /^[0-9]{8,15}$/;
    return re.test(phone);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName || !email || !password || !phone || !confirmPassword) {
      alert("All fields are required.");
      return;
    }

    if (!validateEmail(email)) {
      alert("Invalid email format.");
      return;
    }

    if (!validatePhone(phone)) {
      alert("Invalid phone number.");
      return;
    }

    if (confirmPassword !== password) {
      alert("Password doesn't match");
      return;
    }



    try {

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      setLoading(true);
      const user = userCredential.user;
      await sendEmailVerification(user);

      await setDoc(doc(db, "secondaryAdmin", user.uid), {
        fullName,
        email,
        phone,
        isActiveAdmin: false,
        isSecAdmin: false,
        isAdmin: false,
        isMember: false,
        isCoach: false
      });

      showModal("Sign-up successful! Please verify your email and wait for admin approval.");
      await signOut(auth);

    } catch (error) {
      if (error.code === "auth/email-already-in-use" ||
        (error.error && error.error.message === "EMAIL_EXISTS")) {
        showModal("This email is already registered. Please log in instead.");
      } else {
        showModal(error.message || "An error occurred during sign up. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };


  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(user);
        await signOut(auth);
        alert("Please verify your email before logging in. A new verification email has been sent.");
        setLoading(false);
        return;
      }

      // Prepare both queries
      const adminQuery = query(
        collection(db, "admin"),
        where("uid", "==", user.uid),
        limit(1)
      );
      const secAdminRef = doc(db, "secondaryAdmin", user.uid);

      // Run both queries in parallel
      const [adminQuerySnapshot, secAdminDoc] = await Promise.all([
        getDocs(adminQuery),
        getDoc(secAdminRef)
      ]);

      // Check admin collection
      if (!adminQuerySnapshot.empty) {
        const adminDoc = adminQuerySnapshot.docs[0];
        if (adminDoc.exists() && adminDoc.data().isAdmin) {
          setLoading(false);
          navigate("/admin-dashboard", {
            state: {
              isSecAdmin: false,
              uid: user.uid,
              email: user.email
            }
          });
          return;
        } else {
          alert("Access denied. You are not authorized to access the admin dashboard.");
          navigate("/access-denied");
          setLoading(false);
          return;
        }
      }

      // Check secondaryAdmin collection
      if (secAdminDoc.exists()) {
        const secAdminData = secAdminDoc.data();
        if (!secAdminData.isSecAdmin || !secAdminData.isActiveAdmin) {
          await signOut(auth);
          alert("Your Admin account is not yet approved. Please contact the admin.");
          setLoading(false);
          return;
        }
        setLoading(false);
        navigate("/admin-dashboard", {
          state: {
            isSecAdmin: true,
            uid: user.uid
          }
        });
        return;
      } else {
        await signOut(auth);
        navigate("/access-denied");
        setLoading(false);
        return;
      }
    } catch (error) {
      if (error.code === 'auth/invalid-credential') {
        alert("The barcode, email, or password is incorrect. Please enter correct details.");
      } else {
        console.error(error);
        alert("Failed to Login");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingOverlay isLoading={loading}>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Top bar */}
        <div className="px-4 sm:px-8 pt-5 pb-2">
          <Link to="/" className="inline-flex items-center gap-2 text-green-700 hover:text-green-900 text-sm font-semibold transition-colors">
            <FaArrowAltCircleLeft size={18} />
            Back
          </Link>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
          {/* Logo */}
          <img
            src={logoUrl}
            alt="Gym logo"
            className="h-24 sm:h-32 object-contain mb-6"
          />

          {/* Card */}
          <div className="w-full max-w-sm sm:max-w-md bg-white rounded-2xl shadow-md border border-gray-100 px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 text-center">
              {isSignUp ? "Create Admin Account" : "Admin Portal"}
            </h1>
            <p className="text-sm text-gray-400 text-center mb-7">
              {isSignUp ? "Fill in your details to request access" : "Sign in to manage your gym"}
            </p>

            <form
              className="flex flex-col gap-4"
              onSubmit={isSignUp ? handleSignUp : handleLogin}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            >
              {isSignUp && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sam Wilson"
                    value={fullName}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Address</label>
                <input
                  type="email"
                  placeholder="admin@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                />
                {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    spellCheck="false"
                    className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                  />
                  {password && (
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                      tabIndex="-1"
                    >
                      {showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                    </button>
                  )}
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-0.5">{errors.password}</p>}
              </div>

              {isSignUp && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Confirm Password</label>
                    <div className="relative flex items-center">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="off"
                        data-lpignore="true"
                        data-form-type="other"
                        spellCheck="false"
                        className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                      />
                      {confirmPassword && (
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                          tabIndex="-1"
                        >
                          {showConfirmPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                        </button>
                      )}
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-0.5">{errors.confirmPassword}</p>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone Number</label>
                    <input
                      type="tel"
                      placeholder="e.g. 021 234 5678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="off"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition"
                    />
                  </div>
                </>
              )}

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}
              {errors.general && <p className="text-red-500 text-sm text-center">{errors.general}</p>}

              {!isSignUp && (
                <div className="flex justify-end -mt-1">
                  <Link to="/forgot-password" className="text-xs text-green-600 hover:text-green-800 hover:underline font-medium">
                    Forgot Password?
                  </Link>
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 rounded-lg shadow-sm transition-colors duration-200 text-sm mt-1"
              >
                {isSignUp ? "Request Access" : "Sign In"}
              </button>

              <p className="text-center text-sm text-gray-500 mt-1">
                {isSignUp ? "Already have an account?" : "Need an account?"}{" "}
                <button
                  type="button"
                  onClick={() => setIsSignUp(!isSignUp)}
                  className="text-green-600 hover:text-green-800 font-semibold hover:underline focus:outline-none"
                >
                  {isSignUp ? "Sign In" : "Sign Up"}
                </button>
              </p>
            </form>
          </div>
        </div>

        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
          <p>{modalMessage}</p>
        </Modal>
      </div>
    </LoadingOverlay>
  );

}